import React, { useState } from 'react';
import { Card, Steps } from 'antd';
import FeedbackInfoForm from '../../components/Demo/Feedback/FeedbackInfoForm';
import FeedbackRatingForm from '../../components/Demo/Feedback/FeedbackRatingForm';
import FeedbackThankYou from '../../components/Demo/Feedback/FeedbackThankYou';
import '../../components/Demo/Feedback/KioskFeedbackForm.css';

interface FeedbackData {
  name: string;
  email: string;
  phone?: string;
  organization?: string;
  rating?: number;
  comments?: string;
}

const FeedbackKioskPage: React.FC = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [feedbackData, setFeedbackData] = useState<FeedbackData>({
    name: '',
    email: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const steps = [
    {
      title: 'Information',
      description: 'Tell us about yourself',
    },
    {
      title: 'Rating',
      description: 'Rate your experience',
    },
    {
      title: 'Complete',
      description: 'Thank you!',
    },
  ];

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleInfoSubmit = (data: Partial<FeedbackData>) => {
    setFeedbackData(prev => ({ ...prev, ...data }));
    handleNext();
  };

  const handleRatingSubmit = async (data: { rating: number; comments?: string }) => {
    setIsSubmitting(true);
    try {
      const completeData = { ...feedbackData, ...data };
      
      const response = await fetch('/api/v1/demo/feedback', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...completeData,
          sessionId: `kiosk_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`,
        }),
      });

      if (response.ok) {
        setFeedbackData(prev => ({ ...prev, ...data }));
        handleNext();
      } else {
        throw new Error('Failed to submit feedback');
      }
    } catch (error) {
      console.error('Error submitting feedback:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    setCurrentStep(0);
    setFeedbackData({ name: '', email: '' });
    setIsSubmitting(false);
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <FeedbackInfoForm
            initialData={feedbackData}
            onSubmit={handleInfoSubmit}
            onBack={handlePrev}
            showBackButton={false}
          />
        );
      case 1:
        return (
          <FeedbackRatingForm
            onSubmit={handleRatingSubmit}
            onBack={handlePrev}
            isSubmitting={isSubmitting}
          />
        );
      case 2:
        return (
          <FeedbackThankYou
            name={feedbackData.name}
            onReset={handleReset}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="h-screen bg-gradient-to-br from-blue-50 flex flex-col overflow-hidden" style={{backgroundImage: 'linear-gradient(to bottom right, rgb(239 246 255), #a8ccde)'}}>
      <div className="flex-1 flex flex-col max-w-4xl mx-auto w-full px-4 py-2">
        {/* Header */}
        <div className="text-center mb-4 pt-2">
          <h1 className="text-3xl font-bold text-gray-800 mb-1">
            Feedback
          </h1>
          <p className="text-base text-gray-600">
            We value your feedback and would love to hear from you
          </p>
        </div>

        {/* Progress Steps */}
        <Card className="mb-4 shadow-lg">
          <Steps 
            current={currentStep} 
            items={steps}
            size="small"
            className="px-4 py-2"
          />
        </Card>

        {/* Main Content - Takes remaining space */}
        <Card className="shadow-lg flex-1 flex flex-col">
          <div className=" flex-1 overflow-y-auto">
            {renderStepContent()}
          </div>
        </Card>

        {/* Footer */}
        <div className="text-center mt-2 text-gray-500">
          <p className="text-xs">
            Step {currentStep + 1} of {steps.length}
          </p>
        </div>
      </div>
    </div>
  );
};

export default FeedbackKioskPage;